import type { ErrorInfo, HydrationOptions } from 'react-dom/client';
import {
	addErrorForHydrationStats,
	classifyAsHydrationError,
} from '@jira/hydration-stats-reporting/src/ui/index.tsx';
import {
	extractOwnershipStack,
	getContainingPackageName,
	getOwningTeam,
} from '@jira/platform__ownership-marker/src/ui/extractors.tsx';
// eslint-disable-next-line jira/import/no-dev-code-import-in-app-code
import { autoBeforeEachTest } from '@jira/platform__utils-auto-before-each-test/src/auto-before-each-test.tsx';
import log from '@atlassian/jira-common-util-logging/src/log.tsx';
import { getRouterState } from '@atlassian/react-resource-router/src/controllers/router-store/index.tsx';

const location = 'react-hydrate-root';

const hydrationErrorFiltering = {
	logWarnings: true,
};

autoBeforeEachTest(() => {
	hydrationErrorFiltering.logWarnings = true;
});

export const hydrationOptions: HydrationOptions = {
	onRecoverableError: (untypedError, errorInfo) => {
		if (errorInfo.digest === 'ignore') {
			// the server has told us to ignore this error
			return;
		}
		const error = toTypedError(untypedError);
		const ownershipMetadata = extractOwnershipMetadata(errorInfo);
		const routeMetadata = getRouteMetadata();
		const hydrationErrorType = classifyAsHydrationError(error);
		if (hydrationErrorType !== 'warning' || hydrationErrorFiltering.logWarnings) {
			logToSplunk(error, errorInfo, { ...ownershipMetadata, ...routeMetadata });
			hydrationErrorFiltering.logWarnings = hydrationErrorType !== 'warning';
		}
		addErrorForHydrationStats(error, errorInfo);
		// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
		window.__HYDRATION_RECOVERABLE_ERRORS__ = window.__HYDRATION_RECOVERABLE_ERRORS__ ?? [];
		// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
		window.__HYDRATION_RECOVERABLE_ERRORS__.push({
			error,
			errorInfo,
			ownershipMetadata,
			routeMetadata,
			hydrationErrorType,
		});
	},
};

const toTypedError = (untypedError: unknown) => {
	if (untypedError instanceof Error) {
		return untypedError;
	}
	try {
		return new Error(`Unknown error occurred during hydration: ${JSON.stringify(untypedError)}`);
	} catch (e) {
		return new Error(String(untypedError));
	}
};

type OwnershipMetadata = { teamName?: string; packageName?: string; ownershipStack?: string };
const extractOwnershipMetadata = ({ componentStack }: ErrorInfo): OwnershipMetadata => {
	if (!componentStack) {
		return {};
	}

	const ownershipStack = extractOwnershipStack(componentStack);
	const result: OwnershipMetadata = {
		ownershipStack: JSON.stringify(ownershipStack),
	};

	const teamName = getOwningTeam(ownershipStack);
	if (teamName) {
		result.teamName = teamName;
	}
	const packageName = getContainingPackageName(ownershipStack);
	if (packageName) {
		result.packageName = packageName;
	}
	return result;
};

type RouteMetadata = { routeName?: string };
const getRouteMetadata = (): RouteMetadata => {
	const routerState = getRouterState();
	if (routerState?.route?.name) {
		return { routeName: routerState.route.name };
	}

	return {};
};

type Metadata = OwnershipMetadata & RouteMetadata;
const logToSplunk = (error: Error, errorInfo: ErrorInfo, metadata: Metadata) => {
	const { componentStack, digest } = errorInfo;
	const event: Record<string, string | undefined> = { digest, ...metadata };
	if (componentStack) {
		const lines = componentStack
			.trim()
			.split('\n')
			.map((line) => line.trim());
		if (lines.length > 25) {
			event.shortenedComponentStack = [...lines.slice(0, 20), '...', ...lines.slice(-5)].join('\n');
		} else {
			event.shortenedComponentStack = componentStack;
		}
	}

	log.safeErrorWithoutCustomerData(location, `onRecoverableError: ${error.message}`, event);
};
